import axios from 'axios';
import { refreshTokenService } from './auth.service';
import { decodeJWT } from '../utils/jwt';
import { clearAllUserData } from '../utils/auth-utils';

export const axiosObject = axios.create();
export const authAxios = axios.create();

let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: unknown) => void }[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

function isAuthEndpoint(url: string) {
  return (
    url.includes('/auth/login') ||
    url.includes('/auth/refresh') ||
    url.includes('/auth/forgot-password') ||
    url.includes('/auth/reset-password') ||
    url.includes('/auth/register')
  );
}

function isPublicPage() {
  const path = window.location.pathname;
  return (
    path === '/login' ||
    path === '/signup' ||
    path === '/forgot-password' ||
    path === '/reset-password' ||
    path === '/'
  );
}

async function refreshWithQueue(): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const newToken = await refreshAccessToken();
    processQueue(null, newToken);
    return newToken;
  } catch (error) {
    processQueue(error, null);
    const status = (error as any)?.response?.status;
    if (status === 400 || status === 401 || status === 403) {
      clearAllUserData();
      if (!isPublicPage()) {
        window.location.href = '/login';
      }
    }
    throw error;
  } finally {
    isRefreshing = false;
  }
}

axiosObject.interceptors.request.use(
  async function (config) {
    if (isAuthEndpoint(config.url || '')) {
      return config;
    }

    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (accessToken) {
      try {
        const decoded = JSON.parse(atob(accessToken.split('.')[1] || ''));
        const expirationTime = decoded.exp * 1000;

        if (expirationTime && Date.now() >= expirationTime) {
          if (refreshToken) {
            const newToken = await refreshWithQueue();
            config.headers.Authorization = `Bearer ${newToken}`;
          } else {
            return Promise.reject(new Error('No refresh token available'));
          }
        } else {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } catch {
        if (refreshToken) {
          try {
            const newToken = await refreshWithQueue();
            config.headers.Authorization = `Bearer ${newToken}`;
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
      }
    } else if (refreshToken) {
      try {
        const newToken = await refreshWithQueue();
        config.headers.Authorization = `Bearer ${newToken}`;
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    } else {
      if (!isPublicPage()) {
        return Promise.reject(new Error('No authentication tokens available'));
      }
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axiosObject.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url || '')
    ) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosObject(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        clearAllUserData();
        if (!isPublicPage()) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      try {
        const newToken = await refreshWithQueue();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosObject(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token available');

  const response = await refreshTokenService(refreshToken);
  const decoded = decodeJWT(response.access_token);
  localStorage.setItem('accessToken', response.access_token);
  localStorage.setItem('refreshToken', response.refresh_token);
  localStorage.setItem('expirationTime', `${decoded.exp * 1000}`);
  return response.access_token;
}

export default axiosObject;
